import { Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DataTable } from "../components/table/DataTable";
import { useTranslation } from "react-i18next";
import { TopicPart } from "../../core/topicPart/entities/TopicPart";
import FormLayout from "../components/layout/FormLayout";
import { useTopicFormValidation } from "../../core/hooks/form/useTopicFormValidation";
import {
  emptyTopic,
  Topic,
  TopicFormData,
} from "../../core/topic/entities/Topic";
import topicFormConfig, {
  formHelpConfig,
} from "./config/topic/topicFormConfig";
import Form from "../components/form/Form";
import { useTopicCategoriesStore } from "../../core/store/topicCategoryStore";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { NavigateOptions } from "react-router-dom";
import topicPartTableConfig from "./config/topicPart/topicPartTableConfig";
import { useTrainingRoute } from "../../core/hooks/useTrainingRoute";
import { useAdminPermissions } from "../../core/hooks/useAdminPermissions";
import { getTopicByIdUseCase } from "../../core/topic/useCases/getTopicByIdUseCase";
import { getAllTopicsUseCase } from "../../core/topic/useCases/getAllTopicsUseCase";
import { addUpdateTopicUseCase } from "../../core/topic/useCases/addUpdateTopicUseCase";
import { deleteTopicUseCase } from "../../core/topic/useCases/deleteTopicUseCase";
import { copyTopicContentUseCase } from "../../core/topic/useCases/copyTopicContentUseCase";
import { downloadTopicContentUseCase } from "../../core/topic/useCases/downloadTopicContentUseCase";
import { updateTopicPartOrderUseCase } from "../../core/topicPart/useCases/updateTopicPartOrderUseCase";
import { createSynthesisPartUseCase } from "../../core/topicPart/useCases/createSynthesisPartUseCase";
import PageLayout from "../components/layout/PageLayout";
import { Plus } from "lucide-react";
import { useUnsavedChanges } from "../components/utils/UnsavedChangesContext";

export default function TopicPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    trainingId,
    topicId,
    topicPartId,
    isNew,
    topicPartPath,
    trainingPath,
    topicPath,
  } = useTrainingRoute();
  const permissions = useAdminPermissions();
  const { topicCategories } = useTopicCategoriesStore();
  const { setIsDirty, requestNavigation, bypassGuard } = useUnsavedChanges();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["topic", topicId],
    queryFn: async () => {
      if (!topicId || isNew) {
        return !trainingId ? null : (emptyTopic(trainingId) as Topic);
      }

      return await getTopicByIdUseCase.exec(topicId);
    },
  });

  const prevTopicPartIdRef = useRef<string | undefined>();
  useEffect(() => {
    if (prevTopicPartIdRef.current && !topicPartId) {
      void refetch();
    }
    prevTopicPartIdRef.current = topicPartId;
  }, [topicPartId, refetch]);

  const { data: trainingTopics = [] } = useQuery({
    queryKey: ["topics", trainingId],
    queryFn: () => getAllTopicsUseCase.exec(trainingId!),
    enabled: !!trainingId,
  });

  const topicOptions = useMemo(() => {
    const acc = trainingTopics
      .filter((topic) => topic.id !== data?.id)
      .reduce((map: Record<string, string>, topic) => {
        map[topic.id] = topic.title;
        return map;
      }, {} as Record<string, string>);
    for (const id of data?.relatedTopicIds ?? []) {
      if (id && acc[id] === undefined) {
        acc[id] = `(${id})`;
      }
    }
    return acc;
  }, [trainingTopics, data?.id, data?.relatedTopicIds]);

  const handleReorder = useCallback(
    async (list: TopicPart[]) => {
      const success = await updateTopicPartOrderUseCase.exec(list);
      if (success) refetch();
    },
    [refetch]
  );

  const form = useTopicFormValidation(data ?? null, t);
  const { isDirty } = form.formState;

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const guardedNavigate = useCallback(
    (to: string, options?: NavigateOptions) => {
      requestNavigation(() => navigate(to, options));
    },
    [navigate, requestNavigation]
  );

  const customFormConfig = useMemo(() => {
    const categoryOptions = (arr: typeof topicCategories) =>
      arr.reduce((acc: Record<string, string>, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
    return topicFormConfig(categoryOptions(topicCategories), topicOptions);
  }, [topicCategories, topicOptions]);

  return (
    <>
      <PageLayout isLoading={isLoading}>
        <FormLayout
          title={isNew ? t("topic.new") : data?.title ?? ""}
          onCopy={
            permissions.role === "admin" && data
              ? async () => copyTopicContentUseCase.exec(data)
              : undefined
          }
          onDownload={
            permissions.role === "admin" && data
              ? async () => downloadTopicContentUseCase.exec(data)
              : undefined
          }
          history={
            !isNew && data?.id
              ? {
                  collection: "topics",
                  recordId: data.id,
                  recordLabel: data.title,
                  onRestored: () => void refetch(),
                }
              : undefined
          }
          help={formHelpConfig}
        >
          <Form
            id={data?.id}
            onSave={async (data: TopicFormData, id: string | number | null) => {
              const topic = await addUpdateTopicUseCase.exec(
                data as TopicFormData,
                id
              );

              if (!topic) return;

              if (id) {
                refetch();
              } else {
                bypassGuard(() =>
                  navigate(topicPath(topic.id), { replace: true })
                );
              }
            }}
            onDelete={async (id: string | number) => {
              const success = await deleteTopicUseCase.exec(id);
              if (success) {
                bypassGuard(() => navigate(trainingPath()));
              }
            }}
            form={form}
            formConfig={customFormConfig}
          />
        </FormLayout>
        {!isNew && (
          <DataTable
            title={t("topicPart.title")}
            datas={data?.topicParts ?? []}
            columns={topicPartTableConfig(t, topicPartPath)}
            filters={["title", "description"]}
            onRowClicked={(row: TopicPart) => guardedNavigate(topicPartPath(row.id))}
            onAdd={
              permissions.canCreate
                ? () => guardedNavigate(topicPartPath("new"))
                : undefined
            }
            onReorder={permissions.canReorder ? handleReorder : undefined}
            actions={
              permissions.canCreate ? (
                <button
                  onClick={async () => {
                    const id = await createSynthesisPartUseCase.exec(data?.id);
                    if (id) {
                      guardedNavigate(topicPartPath(id));
                    }
                  }}
                  className="px-2 flex items-center gap-2 border border-gray-300 rounded-3xl hover:bg-blue-600 hover:text-white text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4 " />
                  <span className="text-sm">
                    {t("topicPart.addSynthesisPart")}
                  </span>
                </button>
              ) : undefined
            }
          />
        )}
        <div className="mb-50"></div>
      </PageLayout>
      <Outlet />
    </>
  );
}
